import { createAction as createActionClass } from 'helpers/actionHelpers/createAction'
import { createAction, createPayloadAction, createRequestActionCreators, RequestAction } from 'helpers/actionsHelpers'
import {
  Accomplishment,
  BaseRewardsPayload,
  GetRedemptionAccountResponse,
  GetRedemptionsQueryPayload,
  Redemption,
  RedemptionStepNumber,
  Reward,
  ReferralInfo,
  BountyAppState,
} from 'interfaces/bounty'
import { PayloadAction, SimpleAction } from 'interfaces/action'
import { Paginatable } from 'interfaces/table'
import { GetRewardSummaryResponse, RewardsHistoryItemWithId } from 'components/features/rewards/rewardPage/store'

export const GET_REWARDS = 'GET_REWARDS' as const
type GetRewards = RequestAction<typeof GET_REWARDS, BaseRewardsPayload, Paginatable<RewardsHistoryItemWithId, 'uniqKey'>>
export const getRewardsActionCreator =
  createRequestActionCreators<typeof GET_REWARDS, BaseRewardsPayload, Paginatable<RewardsHistoryItemWithId, 'uniqKey'>>(GET_REWARDS)

type BountyLoadingBlock = 'rewards-details' | 'redemptions'
export const TOGGLE_BOUNTY_BLOCK_LOADING = 'TOGGLE_BOUNTY_BLOCK_LOADING' as const
type ToggleBountyBlockLoading = PayloadAction<typeof TOGGLE_BOUNTY_BLOCK_LOADING, {
  block: BountyLoadingBlock
  toggleValue: boolean
}>
export const toggleBountyBlockLoadingActionCreator = (block: 'rewards-details' | 'redemptions', toggleValue: boolean): ToggleBountyBlockLoading =>
  createPayloadAction(TOGGLE_BOUNTY_BLOCK_LOADING, { block, toggleValue })

export const TOGGLE_REWARD_DETAILS_MODAL = 'TOGGLE_REWARD_DETAILS_MODAL' as const
type ToggleRewardDetailsModal = PayloadAction<typeof TOGGLE_REWARD_DETAILS_MODAL, BountyAppState['reward']['modalType'] | false>
export const toggleRewardDetailsModalActionCreator = (toggleValue: BountyAppState['reward']['modalType'] | false): ToggleRewardDetailsModal =>
  createPayloadAction(TOGGLE_REWARD_DETAILS_MODAL, toggleValue)

export const GET_REDEMPTION_ACCOUNT = 'GET_REDEMPTION_ACCOUNT' as const
type GetRedemptionAccount = RequestAction<typeof GET_REDEMPTION_ACCOUNT, {}, GetRedemptionAccountResponse>
export const getRedemptionAccountActionCreator = createRequestActionCreators<typeof GET_REDEMPTION_ACCOUNT, {}>(GET_REDEMPTION_ACCOUNT)

export const GET_REWARDS_SUMMARY = 'GET_REWARDS_SUMMARY' as const
type GetRewardSummary = RequestAction<typeof GET_REWARDS_SUMMARY, {}, GetRewardSummaryResponse>
export const getRewardSummaryActionCreator = createRequestActionCreators<typeof GET_REWARDS_SUMMARY, {}>(GET_REWARDS_SUMMARY)

const SET_REWARD_SUMMARY_ACTION_TYPE = 'SET_REWARD_SUMMARY' as const
export class SetRewardSummaryAction extends createActionClass<typeof SET_REWARD_SUMMARY_ACTION_TYPE, GetRewardSummaryResponse>(SET_REWARD_SUMMARY_ACTION_TYPE) {}

export const SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE = 'SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE' as const

export type SetRewardsCryptoInvestmentsStateActionPayload = {
  is_autoinvest_on: boolean,
  autoinvest_ticker: string | null,
}

export class SetRewardsCryptoInvestmentsStateAction extends createActionClass<typeof SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE, SetRewardsCryptoInvestmentsStateActionPayload>(SET_REWARD_CRYPTO_AUTOINVESTMENTS_STATE_ACTION_TYPE) {}

export const SET_REDEMPTION_STEP_NUMBER = 'SET_REDEMPTION_STEP_NUMBER' as const
type SetRedemptionStepNumber = PayloadAction<typeof SET_REDEMPTION_STEP_NUMBER, RedemptionStepNumber | null>
export const setRedemptionStepNumberCreator = (redemptionStepNumber: RedemptionStepNumber | null) =>
  createPayloadAction(SET_REDEMPTION_STEP_NUMBER, redemptionStepNumber)

export const GET_REDEMPTIONS = 'GET_REDEMPTIONS' as const
type GetRedemptions = RequestAction<typeof GET_REDEMPTIONS, GetRedemptionsQueryPayload, Paginatable<Redemption, 'id'>>
export const getRedemptionsActionCreator =
  createRequestActionCreators<typeof GET_REDEMPTIONS, GetRedemptionsQueryPayload, Paginatable<Redemption, 'id'>>(GET_REDEMPTIONS)


export const ADD_ACCOMPLISHMENT = 'ADD_ACCOMPLISHMENT' as const
type AddAccomplishment = PayloadAction<typeof ADD_ACCOMPLISHMENT, Accomplishment>
export const addAccomplishmentActionCreator = (accomplishment: Accomplishment) =>
  createPayloadAction(ADD_ACCOMPLISHMENT, accomplishment)

export const RESET_ACCOMPLISHMENTS = 'RESET_ACCOMPLISHMENTS' as const
type ResetAccomplishments = SimpleAction<typeof RESET_ACCOMPLISHMENTS>
export const resetAccomplishmentsActionCreator = createAction(RESET_ACCOMPLISHMENTS)

export const START_REDEMPTION_FLOW = 'START_REDEMPTION_FLOW' as const
type StartRedemptionFlow = SimpleAction<typeof START_REDEMPTION_FLOW>
export const startRedemptionFlowActionCreator = createAction(START_REDEMPTION_FLOW)

export const TOGGLE_DISMISS_REWARD = 'TOGGLE_DISMISS_REWARD' as const
type ToggleDismissReward = RequestAction<typeof TOGGLE_DISMISS_REWARD, Reward, Reward>
export const toggleDismissRewardActionCreator = createRequestActionCreators<
  typeof TOGGLE_DISMISS_REWARD, Reward, Reward
>(TOGGLE_DISMISS_REWARD)

export const GET_REFERRAL_INFO = 'GET_REFERRAL_INFO' as const
type GetReferralInfo = RequestAction<typeof GET_REFERRAL_INFO, {}, ReferralInfo>
export const getReferralInfoActionCreator = createRequestActionCreators<
  typeof GET_REFERRAL_INFO, {}, ReferralInfo
>(GET_REFERRAL_INFO)

type Action =
  | GetRewards
  | ToggleBountyBlockLoading
  | ToggleRewardDetailsModal
  | SetRedemptionStepNumber
  | GetRedemptionAccount
  | GetRewardSummary
  | GetRedemptions
  | AddAccomplishment
  | ResetAccomplishments
  | StartRedemptionFlow
  | ToggleDismissReward
  | GetReferralInfo
  | SetRewardSummaryAction
  | SetRewardsCryptoInvestmentsStateAction

export default Action
